// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import {
//     Box,
//     Typography,
//     Button,
//     RadioGroup,
//     FormControlLabel,
//     Radio,
//     Divider,
//     Stack,
// } from '@mui/material';
// import { assignedCampaignUserAtom, campaignLeadManagementAtom, userInformationAtom } from '../../../Services/_states/index.js';
// import { useRecoilState, useRecoilValue } from 'recoil';
// import { ConstantActions } from '../../../Services/_actions/index.js';
// import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';

// // Types
// interface User {
//     id: string;
//     username: string;
// }

// interface Permission {
//     id: string;
//     access_level: string;
// }

// interface SettingData {
//     permissions?: Permission[];
// }

// interface FieldSettingsProps {
//     settingData?: SettingData;
//     setSettingData: React.Dispatch<React.SetStateAction<SettingData | undefined>>;
//     campaignId: string;
// }

// const ACCESS_LEVELS = [
//     { value: 'view', label: 'View' },
//     { value: 'edit', label: 'Edit' },
//     { value: 'none', label: 'None' },
// ] as const;

// const FieldSettings: React.FC<FieldSettingsProps> = ({
//     settingData,
//     setSettingData,
//     campaignId = '',
// }) => {
//     const constantActions = ConstantActions();
//     const { showSnackBar } = useSnackBar();
//     const loggedInUser = useRecoilValue(userInformationAtom);
//     const organisationId = loggedInUser?.profile?.organisation?.organisation_id;
//     const [userList, setUserList] = useRecoilState(assignedCampaignUserAtom);

//     const getUserMinimalList = useCallback(async () => {
//         if (!campaignId || !organisationId) {
//             return;
//         }

//         // Fix: Check if userList exists and has length > 0, not <= 0
//         if (userList[campaignId] && userList[campaignId].length > 0) {
//             return;
//         }

//         try {
//             const res: any = await constantActions.getUserMinimalList(organisationId, {
//                 campaign_id: campaignId,
//             });
//             setUserList((prev) => ({ ...prev, [campaignId]: res || [] }));
//         } catch (err: any) {
//             showSnackBar(err?.message || err?.toString() || 'Server error. Please try again.', 'error');
//         }
//     }, [campaignId, organisationId, constantActions, setUserList, showSnackBar, userList]);

//     useEffect(() => {
//         getUserMinimalList();
//     }, [getUserMinimalList]);

//     const handleAccessChange = useCallback((id: string, access_level: string) => {
//         setSettingData((prev) => {
//             const currentPermissions = prev?.permissions || [];
//             const existingIndex = currentPermissions.findIndex(perm => perm.id === id);

//             let newPermissions: Permission[];

//             if (existingIndex >= 0) {
//                 // Update existing permission
//                 newPermissions = [...currentPermissions];
//                 newPermissions[existingIndex] = { id, access_level };
//             } else {
//                 // Add new permission
//                 newPermissions = [...currentPermissions, { id, access_level }];
//             }

//             return {
//                 ...prev,
//                 permissions: newPermissions,
//             };
//         });
//     }, [setSettingData]);

//     const getUserPermission = useCallback((id: string) => {
//         return settingData?.permissions?.find(p => p.id === id)?.access_level || '';
//     }, [settingData?.permissions]);

//     // Get current campaign users
//     const currentCampaignUsers = useMemo(() => {
//         return userList[campaignId] || [];
//     }, [userList, campaignId]);

//     if (!settingData) {
//         return (
//             <Typography variant="body2" color="text.secondary">
//                 No attribute information available
//             </Typography>
//         );
//     }

//     return (
//         <Box>
//             <Typography variant="subtitle1" sx={{ mb: 2 }}>
//                 Set access for each user:
//             </Typography>

//             {currentCampaignUsers.length === 0 ? (
//                 <Typography variant="body2" color="text.secondary">
//                     No users found for this campaign
//                 </Typography>
//             ) : (
//                 <Stack spacing={2}>
//                     {currentCampaignUsers?.map((user: User) => (
//                         <Box key={user.id}>
//                             <Stack direction="row" alignItems="center" spacing={2}>
//                                 <Typography
//                                     sx={{
//                                             minWidth: 120,
//                                             fontWeight: 500
//                                         }}
//                                     >
//                                         {user?.username || "Unknown User"}
//                                     </Typography>
//                                     <RadioGroup
//                                         row
//                                         value={getUserPermission(user.id)}
//                                         onChange={(e) =>
//                                             handleAccessChange(user.id, e.target.value)
//                                         }
//                                     >
//                                         {ACCESS_LEVELS.map((level) => (
//                                             <FormControlLabel
//                                                 key={level.value}
//                                                 value={level.value}
//                                                 control={<Radio size="small" />}
//                                                 label={level.label}
//                                             />
//                                         ))}
//                                     </RadioGroup>
//                                 </Stack>
//                                 <Divider sx={{ mt: 1 }} />
//                             </Box>
//                         ))}
//                 </Stack>
//             )}
//         </Box>
//     );
// };

// export default FieldSettings;

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    Stack,
} from '@mui/material';
import { userInformationAtom } from '../../../Services/_states/index.js';
import { useRecoilValue } from 'recoil';
import { ManagementActions } from '../../../Services/_actions/Management/management.actions.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';

// Types
interface Profile {
    id: string;
    name: string;
    organisation?: {
        organisation_id: string;
        name: string;
    };
}

interface Permission {
    id: string;
    access_level: string;
}

interface SettingData {
    permissions?: Permission[];
}

interface FieldSettingsProps {
    settingData?: SettingData;
    setSettingData: React.Dispatch<React.SetStateAction<SettingData | undefined>>;
    campaignId?: string;
}

const ACCESS_LEVELS = [
    { value: 'view', label: 'View' },
    { value: 'edit', label: 'Edit' },
    { value: 'none', label: 'None' },
] as const;

const FieldSettings: React.FC<FieldSettingsProps> = ({
    settingData,
    setSettingData,
    campaignId = '',
}) => {
    const managementActions = ManagementActions();
    const { showSnackBar } = useSnackBar();
    const loggedInUser = useRecoilValue(userInformationAtom);
    const organisationId = loggedInUser?.profile?.organisation?.organisation_id;
    
    const [profileList, setProfileList] = useState<Profile[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const getProfileList = useCallback(async () => {
        if (!organisationId) {
            showSnackBar('Organisation ID not found', 'warning');
            return;
        }

        // Check if we already have profiles loaded
        if (profileList.length > 0) {
            return;
        }

        setIsLoading(true);
        try {
            const res: any = await managementActions.getProfileList({
                organisation_id: organisationId,
                limit: 100, // Get all profiles
                page: 0,
                search: '',
                sortOrder: 'asc',
                sortField: 'name',
            });
            
            if (res?.status === 200 && res?.data?.list) {
                setProfileList(res.data.list || []);
            } else {
                showSnackBar('No profiles found', 'info');
            }
        } catch (err: any) {
            console.error('Error fetching profiles:', err);
            showSnackBar(err?.message || err?.toString() || 'Failed to fetch profiles. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [organisationId, managementActions, showSnackBar, profileList.length]);

    useEffect(() => {
        getProfileList();
    }, [getProfileList]);

    const handleAccessChange = useCallback((profileId: string, access_level: string) => {
        setSettingData((prev) => {
            const currentPermissions = prev?.permissions || [];
            const existingIndex = currentPermissions.findIndex(perm => perm.id === profileId);

            let newPermissions: Permission[];

            if (existingIndex >= 0) {
                // Update existing permission
                newPermissions = [...currentPermissions];
                newPermissions[existingIndex] = { id: profileId, access_level };
            } else {
                // Add new permission
                newPermissions = [...currentPermissions, { id: profileId, access_level }];
            }

            return {
                ...prev,
                permissions: newPermissions,
            };
        });
    }, [setSettingData]);

    const getProfilePermission = useCallback((profileId: string) => {
        return settingData?.permissions?.find(p => p.id === profileId)?.access_level || '';
    }, [settingData?.permissions]);

    // Memoize the profile list to avoid unnecessary re-renders
    const sortedProfiles = useMemo(() => {
        return [...profileList].sort((a, b) => 
            (a.name || '').localeCompare(b.name || '')
        );
    }, [profileList]);

    if (!settingData) {
        return (
            <Typography variant="body2" color="text.secondary">
                No attribute information available
            </Typography>
        );
    }

    if (isLoading) {
        return (
            <Typography variant="body2" color="text.secondary">
                Loading profiles...
            </Typography>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Set access level for each profile:
            </Typography>

            {sortedProfiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No profiles found for this organisation
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {sortedProfiles.map((profile: Profile) => (
                        <Box key={profile.id}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography
                                    sx={{
                                        minWidth: 200,
                                        fontWeight: 500,
                                        color: 'text.primary',
                                    }}
                                >
                                    {profile?.name || "Unknown Profile"}
                                </Typography>
                                <RadioGroup
                                    row
                                    value={getProfilePermission(profile.id)}
                                    onChange={(e) =>
                                        handleAccessChange(profile.id, e.target.value)
                                    }
                                >
                                    {ACCESS_LEVELS.map((level) => (
                                        <FormControlLabel
                                            key={level.value}
                                            value={level.value}
                                            control={<Radio size="small" />}
                                            label={level.label}
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.875rem',
                                                }
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </Stack>
                            <Divider sx={{ mt: 1 }} />
                        </Box>
                    ))}
                </Stack>
            )}

            {sortedProfiles.length > 0 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        <strong>Note:</strong> These permissions will apply to all users within each profile. 
                        Users inherit the permissions from their assigned profile.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default FieldSettings;